import React from "react";
import { CheckCircle } from "lucide-react";
import { processSteps } from "../data/mock";

const ProcessSection = ({ onOpenForm }) => {
  return (
    <section id="process" className="section-padding bg-gradient-to-br from-[#1e3a8a] to-[#1e40af] text-white relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#d4af37] rounded-full blur-3xl"></div>
      </div>

      <div className="container-custom px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">
            Our Process
          </span>
          <h2 className="text-4xl md:text-5xl font-bold mt-3 mb-6">
            Simple 3-Step Trademark Process
          </h2>
          <p className="text-lg text-white/90 max-w-3xl mx-auto leading-relaxed">
            Our streamlined trademark registration process makes protecting your brand straightforward and stress-free. 
            From initial consultation to final registration, we handle every detail with precision and expertise, 
            ensuring your trademark application has the best chance of success.
          </p>
        </div>

        {/* Process Steps */}
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {processSteps.map((step, index) => (
            <div key={index} className="relative">
              {/* Connector Line - Desktop Only */}
              {index < processSteps.length - 1 && (
                <div className="hidden md:block absolute top-16 left-[60%] w-[80%] h-1 bg-[#d4af37]/30">
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-[#d4af37] rotate-45"></div>
                </div>
              )}

              {/* Step Card */}
              <div className="relative bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:bg-white/15 transition-all duration-300 h-full">
                {/* Step Number */}
                <div className="w-16 h-16 bg-gradient-secondary rounded-full flex items-center justify-center mb-6 shadow-xl">
                  <span className="text-3xl font-bold text-[#1e3a8a]">{step.step}</span>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-bold mb-4 text-white">
                  {step.title}
                </h3>
                <p className="text-white/80 leading-relaxed">
                  {step.description}
                </p>

                {/* Check Icon */}
                <div className="mt-6">
                  <CheckCircle className="w-8 h-8 text-[#d4af37]" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Process Details */}
        <div className="mt-20 max-w-4xl mx-auto">
          <div className="bg-white/10 backdrop-blur-sm p-8 md:p-12 rounded-2xl border border-white/20">
            <h3 className="text-2xl md:text-3xl font-bold mb-6 text-center">
              What Happens After You Submit Your Trademark Application?
            </h3>
            
            <div className="space-y-6 text-white/90 leading-relaxed">
              <p>
                Once your trademark application is filed with the USPTO, it enters an examination phase where a 
                trademark examining attorney reviews your application for compliance with federal law and regulations. 
                This process typically takes 3-4 months from the filing date.
              </p>
              
              <p>
                During the examination, the attorney will check for conflicts with existing trademarks and ensure your 
                application meets all technical requirements. If any issues arise, they'll issue an "office action" 
                that our experienced attorneys will promptly address on your behalf.
              </p>
              
              <p>
                If your trademark is approved, it will be published in the USPTO's Official Gazette for a 30-day 
                opposition period, during which third parties can oppose your registration. If no opposition is filed 
                or any opposition is overcome, your trademark will proceed to final registration.
              </p>
              
              <p>
                Throughout this entire process, we monitor your application status, handle all correspondence with the 
                USPTO, and keep you informed every step of the way. Our dedicated case managers ensure your trademark 
                registration proceeds smoothly from start to finish.
              </p>
            </div>

            <div className="mt-8 pt-8 border-t border-white/20 text-center">
              <p className="text-lg font-semibold text-white mb-2">
                Average Timeline: 8-12 Months from Filing to Registration
              </p>
              <p className="text-white/80">
                We'll keep you updated throughout the entire process
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <button onClick={onOpenForm} className="btn-secondary text-lg px-10 py-4">
            Start Your Trademark Journey Today
          </button>
        </div>
      </div>
    </section>
  );
};

export default ProcessSection;
